import { Loading, useOrgUser, useSendInvite, useWindowTitle } from '@cibo/profile'
import {
  FriendlyError,
  FullScreenError,
  intuitiveFromNow,
  logRequestError,
  useFigures,
  useParamAsInteger,
} from '@cibo/ui'
import RefreshRounded from '@mui/icons-material/RefreshRounded'
import { LoadingButton } from '@mui/lab'
import {
  Avatar,
  Box,
  Divider,
  Grid,
  Link as MuiLink,
  Stack,
  Typography,
  TypographyProps,
} from '@mui/material'
import { StatusCodes } from 'http-status-codes'
import { ADMIN_PATHS, ADMIN_ROUTES } from 'navigation/types'
import { useSnackbar } from 'notistack'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, Outlet, generatePath, useLocation } from 'react-router-dom'

const Label: FC<TypographyProps> = props => (
  <Typography variant="body2" color="text.secondary" {...props} />
)

export const UserLayout: FC = ({ children }) => {
  const { t } = useTranslation('impact/UserProfile')
  useWindowTitle(t('pageTitle'))
  const userId = useParamAsInteger('userId')

  const { pathname } = useLocation()
  const { earnedAmount } = useFigures()
  const sendInvite = useSendInvite()
  const orgUserId = userId
  const userQuery = useOrgUser(orgUserId, {
    refetchOnMount: 'always',
  })

  const userItem = userQuery.data
  const paths = pathname.split('/')
  const { enqueueSnackbar } = useSnackbar()
  const handleSendInvite = () => {
    if (!userId) return
    sendInvite
      .mutateAsync({ userId, alwaysSend: true })
      .then(() => enqueueSnackbar(t('inviteSentSuccess'), { variant: 'success' }))
      .catch(error => {
        logRequestError(error)
        enqueueSnackbar(t('inviteSentError'), { variant: 'error' })
      })
  }

  if (!orgUserId) {
    return <FullScreenError status={StatusCodes.NOT_FOUND} />
  }

  if (userQuery.isError) {
    return <FriendlyError message={t('notFound')} />
  }
  if (userQuery.isInitialLoading || !userItem) {
    return <Loading />
  }

  if (paths.includes(ADMIN_PATHS.USER_FIELDS_ADD)) {
    return <Outlet />
  }

  const {
    givenName,
    familyName,
    email,
    lastLoginDate,
    allTimeEarnings,
    org,
    advisor,
  } = userItem.user

  return (
    <>
      <Grid container flex={1}>
        <Grid item xs={12} md={3} lg={2} p={3} overflow="hidden">
          <Stack spacing={2} divider={<Divider />}>
            <Stack spacing={2}>
              <Avatar
                sx={{
                  width: 96,
                  height: 96,
                  fontSize: '2.5rem',
                  marginBottom: 2,
                  alignSelf: 'center',
                }}
              >
                {!!givenName || !!familyName
                  ? `${!!givenName?.charAt(0) ? givenName?.charAt(0) : ''}${
                      familyName?.charAt(0) ? familyName?.charAt(0) : ''
                    }`
                  : null}
              </Avatar>
              <Stack>
                <Typography variant="h6" sx={{ wordWrap: 'break-word' }}>
                  {userItem.displayNameFull}
                </Typography>

                <Typography variant="caption" sx={{ wordWrap: 'break-word' }}>
                  {email}
                </Typography>
              </Stack>

              {!!lastLoginDate ? (
                <Stack>
                  <Label>{t('lastLogin')}</Label>
                  <Typography>{intuitiveFromNow(lastLoginDate)}</Typography>
                </Stack>
              ) : (
                <>
                  <Stack>
                    <Label>{t('lastLogin')}</Label>
                    <Typography>{t('never')}</Typography>
                  </Stack>
                  <Stack>
                    <Label>{t('inviteSent')}</Label>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography>
                        {t('inviteSent', {
                          context: JSON.stringify(!!userItem.user.hasSentInvite),
                        })}
                      </Typography>
                      {/* @todo CPD-2517 guard with <Can useAny={AuthUserPermission.USER_RESEND_INVITE}> */}
                      <Box>
                        <LoadingButton
                          onClick={handleSendInvite}
                          loading={sendInvite.isLoading}
                          endIcon={<RefreshRounded />}
                          size="small"
                        >
                          {t('sendInvite', {
                            context: JSON.stringify(!!userItem.user.hasSentInvite),
                          })}
                        </LoadingButton>
                      </Box>
                    </Stack>
                  </Stack>
                </>
              )}
            </Stack>

            {!!org && (
              <Stack spacing={2}>
                {!!org.label && (
                  <Stack>
                    <Label>{t('organization')}</Label>
                    <MuiLink
                      component={Link}
                      color="secondary"
                      underline="hover"
                      to={generatePath(ADMIN_ROUTES.ORGANIZATION_DETAIL, {
                        organizationId: org.id,
                      })}
                    >
                      {org.label}
                    </MuiLink>
                  </Stack>
                )}

                {advisor && (
                  <Stack>
                    <Label>{t('relationshipManager')}</Label>
                    <MuiLink
                      component={Link}
                      color="secondary"
                      underline="hover"
                      to={generatePath(ADMIN_ROUTES.USER_DETAIL, {
                        userId: advisor.userId.toString(),
                      })}
                    >
                      {userItem.advisorDisplayNameFull}
                    </MuiLink>
                  </Stack>
                )}
              </Stack>
            )}

            {!!allTimeEarnings && (
              <Stack>
                <Label>{t('earnings')}</Label>
                <Typography>{earnedAmount({ amount: allTimeEarnings, token: 'usd' })}</Typography>
                <Typography variant="caption">{t('allTime')}</Typography>
              </Stack>
            )}
          </Stack>
        </Grid>

        <Grid item xs={12} md={9} lg={10} p={2} flex={1} display="flex">
          {children}
        </Grid>
      </Grid>
    </>
  )
}
